import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
// Used only by the Out of Network coverage fields (now commented out below)
// import PercentIcon from "@mui/icons-material/Percent";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  // Used only by the Out of Network coverage fields (now commented out below)
  // FormControlLabel,
  Grid,
  // IconButton,
  // InputAdornment,
  // Radio,
  // RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useMemo, useState } from "react";
import {
  INITIAL_PATIENT_INSURANCE,
  MAX_INSURANCE_EFFECTIVE_DATE,
  MIN_INSURANCE_EFFECTIVE_DATE,
  NewPatientForm,
} from "@/constants/provider/patient/add-new-patient/add-new-patient-form";
import { useDebounce } from "@/hooks/use-debounce";
import useDisclosure from "@/hooks/use-disclosure";
import { useReduceDisclosure } from "@/hooks/use-reduce-disclosure";
import CheckEligibilityModal from "@/pages/app/provider/patient/patient-list/patient-chart-new/demographics/eligibility/CheckEligibilityModal";
import EligibilityReportModal from "@/pages/app/provider/patient/patient-list/patient-chart-new/demographics/eligibility/EligibilityReportModal";
import { useGetAllInsurancePlan } from "@/queries/insurance/use-get-all-insurance-plan";
import { useProvidersList } from "@/queries/provider/setting/use-providers-list";
import { useInsurancePayerList } from "@/queries/use-insurance-payer";
import { convertToBase64, DEF_PAGINATION, DGT_Pagination } from "@/utils/utils";
import AsyncSelect, { AsyncSelectOption } from "@/components/AsyncSelect";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import CustomDatePicker from "@/components/CustomDatePicker";
import CustomSelect from "@/components/CustomSelect";
import Flex from "@/components/Flex";
import Input from "@/components/Input";
import OutlineCard from "@/components/OutlineCard";
import CustomTitle from "@/components/app/CustomTitle";
import DropZone from "@/components/app/DropZone";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import EmployerInfo from "./insurance/EmployerInfo";
import { getInsuranceFieldError } from "./insurance/insurance-validation";
import PayerDetailsDrawer from "./insurance/PayerDetailsDrawer";
import SubscriberInformation from "./insurance/SubscriberInformation";
import NumberInput from "@/components/NumberInput";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useRoleController } from "@/components/useRoleController";
import { getStoredRealm } from "@/utils/auth-storage";
interface InsuranceProps {
  formik: FormikProps<NewPatientForm>;
  needSectionLabel?: boolean;
  addOneInsurance?: boolean;
  patientData?: any;
  // When false, the "Check Eligibility" action is hidden. The add/edit patient
  // flows only maintain the manually-selected Network Status and do not run the
  // eligibility check; other flows (insurance drawers) keep the action.
  showCheckEligibility?: boolean;
}
const LinkButtons = ({
  onOpen,
  setEdit,
}: {
  onOpen: any;
  setEdit: (value: boolean) => void;
}) => {
  const handleToggleEdit = () => {
    setEdit(true);
    onOpen();
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" ml={0.2}>
      {/* <Button
        variant="text"
        color="primary"
        sx={{ textTransform: "none", minWidth: 0, p: 0 }}
        onClick={() => {
          setEdit(false);
          disclosureHost.open();
        }}
      >
        Show payer details
      </Button> */}

      {/* <Typography color="primary">|</Typography> */}

      <Button
        variant="text"
        color="primary"
        sx={{ textTransform: "none", minWidth: 0 }}
        onClick={handleToggleEdit}
      >
        Edit payer and plan details
      </Button>
    </Stack>
  );
};

export const planTypeMapping: Record<string, string> = {
  PPO: "Medical",
  HMO: "Medical",
  MEDICARE: "Medical",
  DENTAL: "Dental",
  VISION: "Other",
  HSA: "Medical",
  PRIVATE: "Medical",
  EPO: "Medical",
  POS: "Medical",
  OTHER: "Medical",
  MEDICAID: "Medical",
  WORKERS_COMPENSATION: "Worker Compensation",
  BEHAVIORAL_HEALTH: "Behavioral Health",
  MOTOR_VEHICLE: "Motor Vehicle",
  UNINSURED: "Uninsured",
};

const Insurance = ({
  formik,
  needSectionLabel = true,
  addOneInsurance = true,
  patientData,
  showCheckEligibility = true,
}: InsuranceProps) => {
  const { values, handleBlur, handleChange, setFieldValue, errors } = formik;
  const [searchProviderKey, setSearchProviderKey] = useState<string>("");
  const [searchInsKey, setSearchInsKey] = useDebounce<string>("");
  const [searchPayerKey, setSearchPayerKey] = useState<string>("");
  const [payerPagination, setPayerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  const [isEdit, setIsEdit] = useState(false);
  const [accordionOpenMap, setAccordionOpenMap] = useState<
    Record<string, boolean>
  >({});
  const payerDetailsDisclosure = useDisclosure(false);
  const [checkEligibilityDisclosure, eligibilityReportDisclosure] =
    useReduceDisclosure(2);

  // ECRH-5271: prefill the Check Eligibility modal with the Payer Name and
  // Provider from the insurance record the user clicked "Check Eligibility" on.
  const [eligibilityPrefill, setEligibilityPrefill] = useState<{
    prefillIns?: { insuranceId: string; label: string; value: string };
    prefillProvider?: AsyncSelectOption;
  }>({});

  // ECRH-4589: role-based access for eligibility & coverage override fields.
  // hasUpdate -> can edit/override coverage fields (hidden otherwise);
  // hasView -> can see the Check Eligibility action.
  const { hasUpdate: hasUpdateEligibility, hasView: hasViewEligibility } =
    useRoleController("Check Eligibility");

  // Restrict Effective Start Date selection to on/after the Patient DOB.
  // Falls back to the global minimum when DOB is unavailable.
  const insuranceMinDate = useMemo(() => {
    const dobValue =
      (values as { birthDate?: string | null })?.birthDate ??
      patientData?.birthDate;
    return dobValue && dayjs(dobValue).isValid()
      ? dayjs(dobValue).startOf("day")
      : MIN_INSURANCE_EFFECTIVE_DATE;
  }, [
    (values as { birthDate?: string | null })?.birthDate,
    patientData?.birthDate,
  ]);

  const {
    data: insurancePayerList,
    isLoading: insurancePayerListLoading,
    isFetching: insurancePayerListFetching,
  } = useInsurancePayerList({
    searchKey: searchInsKey,
    pagination: payerPagination,
  });

  const { data: insurancePlanList, isLoading: insurancePlanListLoading } =
    useGetAllInsurancePlan({
      status: true,
      pagination: {
        page: 0,
        pageSize: 20,
      },
      payerId:
        formik.values.patientInsurances?.[0]?.insurancePayer?.payerId?.value ??
        "",
      searchKey: searchPayerKey,
      xTENANTID: getStoredRealm() ?? "",
    });

  const { data: providersList, isFetching: providersListFetching } =
    useProvidersList({
      status: true,
      pagination: providerPagination,
      searchKey: searchProviderKey,
      xTENANTID: getStoredRealm() ?? "",
    });

  const [me] = useCurrentUser();

  return (
    <Box mt={1}>
      <PayerDetailsDrawer
        isEdit={isEdit}
        disclosureHost={payerDetailsDisclosure}
      />
      {checkEligibilityDisclosure.opened && (
        <CheckEligibilityModal
          disclosureHost={checkEligibilityDisclosure}
          eligibilityReportDisclosure={eligibilityReportDisclosure}
          prefillIns={eligibilityPrefill.prefillIns}
          prefillProvider={{
            value: me?.uuid ?? "",
            label: `${me?.firstName} ${me?.lastName}`,
          }}
          onEligibilityChecked={() => {}}
        />
      )}
      <EligibilityReportModal disclosureHost={eligibilityReportDisclosure} />
      {formik.values.patientInsurances?.map((_, ind) => (
        <Box key={ind} id={"Insurance"} mb={1}>
          <Flex justifyContent={"space-between"} alignItems={"center"}>
            {needSectionLabel && <CustomTitle>Insurance</CustomTitle>}
            {ind !== 0 && (
              <StyledButtonV2
                onClick={() =>
                  formik.setFieldValue(
                    "patientInsurances",
                    formik.values.patientInsurances?.toSpliced(ind, 1)
                  )
                }
                variant="text"
                color="primary"
                label="Remove"
                sIcon={<HighlightOffOutlinedIcon />}
              />
            )}
          </Flex>
          <OutlineCard
            sx={{
              padding: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Grid item container spacing={3}>
              {/* <Grid item xs={12}>
                <Typography fontWeight={500}>Insurance Details</Typography>
              </Grid> */}
              {/* <Grid item xs={12}>
                <Grid item xs={4}>
                  <Flex alignItems={"center"} gap={1}>
                    <ValueSwitcher
                      alignment={
                        !!values.patientInsurances?.[ind]?.active
                          ? "left"
                          : "right"
                      }
                      handleChange={(_, value) => {
                        setFieldValue(
                          `patientInsurances[${ind}].active`,
                          value === "left"
                        );
                      }}
                      timing={[
                        {
                          value: "left",
                          label: "Active",
                        },
                        {
                          value: "right",
                          label: "Inactive",
                        },
                      ]}
                    />
                  </Flex>
                </Grid>
              </Grid> */}
              <Grid item xs={12}>
                <AsyncSelectScroll
                  loading={
                    insurancePayerListFetching || insurancePayerListLoading
                  }
                  pagination={payerPagination}
                  setPagination={setPayerPagination}
                  totalElements={insurancePayerList?.data?.page?.totalElements}
                  value={
                    values.patientInsurances?.[ind]?.insurancePayer?.payerId
                  }
                  onChange={(opt) => {
                    setFieldValue(
                      `patientInsurances.[${ind}].insurancePayer.payerId`,
                      opt
                    );
                    // logic behind this
                    setFieldValue(
                      `patientInsurances.[${ind}].planType`,
                      undefined
                    );
                    setFieldValue(
                      `patientInsurances.[${ind}].coverageType`,
                      undefined
                    );
                    setFieldValue(
                      `patientInsurances.[${ind}].planId`,
                      undefined
                    );
                    setSearchPayerKey(opt?.value ?? "");
                  }}
                  onSearch={(val) => {
                    setSearchInsKey(val);
                    setPayerPagination((p) => ({ ...p, page: 0 }));
                  }}
                  enableFilterOption
                  options={insurancePayerList?.data?.content?.map(
                    (insurance) => ({
                      value: insurance.payerId,
                      label: insurance.payerName,
                    })
                  )}
                  label="Payer Name"
                  // placeholder="Select"
                />
              </Grid>

              <Grid item xs={3}>
                <Box sx={{ Height: "auto" }}>
                  <AsyncSelect
                    loading={insurancePlanListLoading}
                    value={values.patientInsurances?.[ind]?.planId}
                    onChange={(value) => {
                      setFieldValue(`patientInsurances.[${ind}].planId`, value);
                      setFieldValue(
                        `patientInsurances.[${ind}].planName`,
                        value
                      );

                      const selectedInsurance =
                        insurancePlanList?.data?.content?.find(
                          (insurance) => insurance.uuid === value?.value
                        );
                      if (selectedInsurance) {
                        setFieldValue(
                          `patientInsurances.[${ind}].planType`,
                          selectedInsurance?.planType
                        );
                        setFieldValue(
                          `patientInsurances.[${ind}].coverageType`,
                          selectedInsurance?.coverageType
                        );
                      } else {
                        setFieldValue(
                          `patientInsurances.[${ind}].planType`,
                          undefined
                        );
                        setFieldValue(
                          `patientInsurances.[${ind}].coverageType`,
                          undefined
                        );
                      }
                    }}
                    onSearch={setSearchPayerKey}
                    options={insurancePlanList?.data?.content?.map(
                      (insurance) => ({
                        value: insurance.uuid,
                        label: insurance.planName,
                      })
                    )}
                    label="Plan Name"
                    // placeholder="Select"
                  />

                  <LinkButtons
                    setEdit={setIsEdit}
                    onOpen={() =>
                      payerDetailsDisclosure.open(
                        values.patientInsurances?.[ind]?.insurancePayer?.payerId
                      )
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Input
                  label="Plan Type"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].planType`,
                    value:
                      values.patientInsurances?.[ind]?.planType?.replaceAll(
                        "_",
                        " "
                      ) ?? "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                    // placeholder: "Plan Type",
                    disabled: true,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  label="Coverage Type"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].coverageType`,
                    value: values.patientInsurances?.[ind]?.coverageType ?? "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                    // placeholder: "Coverage Type",
                    disabled: true,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomSelect
                  disableSortOptions
                  label="Order of Benefits"
                  value={values.patientInsurances?.[ind]?.orderOfBenefits}
                  options={[
                    { value: "PRIMARY", label: "Primary" },
                    { value: "SECONDARY", label: "Secondary" },
                    { value: "TERNARY", label: "Tertiary" },
                    { value: "OTHER", label: "Other" },
                  ]}
                  onChange={(value) =>
                    setFieldValue(
                      `patientInsurances[${ind}].orderOfBenefits`,
                      value
                    )
                  }
                  error={
                    (
                      formik.errors?.patientInsurances?.[ind] as {
                        orderOfBenefits?: string;
                      }
                    )?.orderOfBenefits as string
                  }
                  // placeholder="Order of Benefits"
                />
              </Grid>

              <Grid item xs={3}>
                <Input
                  label="Insurance ID"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].insuranceId`,
                    value: values.patientInsurances?.[ind]?.insuranceId,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    // placeholder: "Enter Insurance ID",
                  }}
                  error={getInsuranceFieldError(formik, ind, "insuranceId")}
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  label="Member ID"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].memberId`,
                    value: values.patientInsurances?.[ind]?.memberId ?? "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  }}
                  error={getInsuranceFieldError(formik, ind, "memberId")}
                />
              </Grid>
              <Grid item xs={3}>
                <Input
                  label="Group ID"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].groupId`,
                    value: values.patientInsurances?.[ind]?.groupId,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    // placeholder: "Enter Group ID",
                  }}
                  error={getInsuranceFieldError(formik, ind, "groupId")}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomDatePicker
                  label="Effective Start Date"
                  minDate={insuranceMinDate}
                  maxDate={MAX_INSURANCE_EFFECTIVE_DATE}
                  value={values.patientInsurances?.[ind]?.effectiveStartDate}
                  onChange={(value) => {
                    setFieldValue(
                      `patientInsurances[${ind}].effectiveStartDate`,
                      value
                    );
                  }}
                  error={
                    ((
                      formik.touched?.patientInsurances as unknown as {
                        effectiveStartDate?: boolean;
                      }[]
                    )?.[ind]?.effectiveStartDate &&
                      (
                        formik.errors?.patientInsurances?.[ind] as {
                          effectiveStartDate?: string;
                        }
                      )?.effectiveStartDate) as string
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <CustomDatePicker
                  label="Effective End Date"
                  minDate={
                    values.patientInsurances?.[ind]?.effectiveStartDate
                      ? dayjs(
                          values.patientInsurances?.[ind]?.effectiveStartDate
                        ).add(1, "day")
                      : MIN_INSURANCE_EFFECTIVE_DATE
                  }
                  maxDate={MAX_INSURANCE_EFFECTIVE_DATE}
                  value={values.patientInsurances?.[ind]?.effectiveEndDate}
                  onChange={(value) => {
                    setFieldValue(
                      `patientInsurances[${ind}].effectiveEndDate`,
                      value
                    );
                  }}
                  error={
                    ((
                      formik.touched?.patientInsurances as unknown as {
                        effectiveEndDate?: boolean;
                      }[]
                    )?.[ind]?.effectiveEndDate &&
                      (
                        formik.errors?.patientInsurances?.[ind] as {
                          effectiveEndDate?: string;
                        }
                      )?.effectiveEndDate) as string
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <AsyncSelectScroll
                  label="Provider"
                  // placeholder="Select Provider"
                  loading={providersListFetching}
                  options={providersList?.data?.content?.map((it) => ({
                    value: it?.uuid,
                    label: it?.firstName + " " + it?.lastName,
                  }))}
                  onSearch={(val) => {
                    setSearchProviderKey(val);
                    setProviderPagination((p) => ({ ...p, page: 0 }));
                  }}
                  value={values.patientInsurances?.[ind]?.provider}
                  onChange={(option) => {
                    setFieldValue(`patientInsurances[${ind}].provider`, option);
                  }}
                  pagination={providerPagination}
                  setPagination={setProviderPagination}
                  totalElements={providersList?.data?.page?.totalElements}
                />
              </Grid>
              {hasUpdateEligibility && (
                <Grid item xs={3}>
                  <CustomSelect
                    label="Network Status"
                    value={values.patientInsurances?.[ind]?.networkStatus}
                    options={[
                      { value: "IN_NETWORK", label: "In Network" },
                      { value: "OUT_OF_NETWORK", label: "Out of Network" },
                    ]}
                    onChange={(value) => {
                      setFieldValue(
                        `patientInsurances[${ind}].networkStatus`,
                        value
                      );
                      // Clear Out of Network fields
                      setFieldValue(
                        `patientInsurances[${ind}].copayAmount`,
                        undefined
                      );
                      setFieldValue(
                        `patientInsurances[${ind}].coInsuranceAmount`,
                        undefined
                      );
                      setFieldValue(
                        `patientInsurances[${ind}].coverageLevel`,
                        undefined
                      );
                      setFieldValue(
                        `patientInsurances[${ind}].priorAuthorization`,
                        undefined
                      );
                      setFieldValue(
                        `patientInsurances[${ind}].verificationStatus`,
                        undefined
                      );
                    }}
                  />
                </Grid>
              )}
              {showCheckEligibility &&
                values.patientInsurances?.[ind]?.networkStatus ===
                  "IN_NETWORK" &&
                hasViewEligibility && (
                  <Grid item xs={3} display="flex" alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={
                        !values.patientInsurances?.[ind]?.insurancePayer
                          ?.payerId?.value ||
                        !values.patientInsurances?.[ind]?.planId?.value ||
                        !values.patientInsurances?.[ind]?.insuranceId ||
                        !values.patientInsurances?.[ind]?.groupId
                      }
                      onClick={() => {
                        const rec = values.patientInsurances?.[ind];
                        const payer = rec?.insurancePayer?.payerId;
                        setEligibilityPrefill({
                          prefillIns: rec?.insuranceId
                            ? {
                                insuranceId: rec.insuranceId,
                                label: payer?.label ?? "",
                                value: rec?.uuid ?? payer?.value ?? "",
                              }
                            : undefined,
                          prefillProvider: rec?.provider ?? undefined,
                        });
                        checkEligibilityDisclosure.open();
                      }}
                      sx={{ textTransform: "none", height: "fit-content" }}
                    >
                      Check Eligibility
                    </Button>
                  </Grid>
                )}

              {/* Coverage Details fields and the Pre-Authorization Required
                  field are no longer displayed for Out of Network patients
                  during manual patient registration. Kept commented for
                  reference.
              {values.patientInsurances?.[ind]?.networkStatus ===
                "OUT_OF_NETWORK" &&
                hasUpdateEligibility && (
                  <>
                    <Grid item container spacing={3} xs={12}>
                      <Grid item xs={12}>
                        <Typography fontWeight={500}>
                          Coverage Details
                        </Typography>
                      </Grid>

                      <Grid item xs={3}>
                        <NumberInput
                          label="Copay Amount"
                          textFieldProps={{
                            name: `patientInsurances[${ind}].copayAmount`,
                            value:
                              values.patientInsurances?.[ind]?.copayAmount ??
                              "",
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                        />
                      </Grid>

                      <Grid item xs={3}>
                        <Input
                          label="Co-Insurance %"
                          textFieldProps={{
                            name: `patientInsurances[${ind}].coInsuranceAmount`,
                            value:
                              values.patientInsurances?.[ind]
                                ?.coInsuranceAmount ?? "",
                            onChange: handleChange,
                            onBlur: handleBlur,
                            InputProps: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton>
                                    <PercentIcon sx={{ height: "1.2rem" }} />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <NumberInput
                          label="Deductible Amount"
                          textFieldProps={{
                            name: `patientInsurances[${ind}].deductible`,
                            value:
                              values.patientInsurances?.[ind]?.deductible ?? "",
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <NumberInput
                          label="Deductible Remaining"
                          textFieldProps={{
                            name: `patientInsurances[${ind}].deductibleAmount`,
                            value:
                              values.patientInsurances?.[ind]
                                ?.deductibleAmount ?? "",
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Input
                          label="Coverage Level"
                          textFieldProps={{
                            name: `patientInsurances[${ind}].coverageLevel`,
                            value:
                              values.patientInsurances?.[ind]?.coverageLevel ??
                              "",
                            onChange: handleChange,
                            onBlur: handleBlur,
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Grid
                      item
                      container
                      spacing={3}
                      xs={12}
                      alignItems="center"
                    >
                      <Grid item xs={3}>
                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                          Pre-Authorization Required?
                        </Typography>
                        <RadioGroup
                          row
                          name={`patientInsurances[${ind}].priorAuthorization`}
                          value={
                            values.patientInsurances?.[ind]
                              ?.priorAuthorization === true
                              ? "true"
                              : values.patientInsurances?.[ind]
                                    ?.priorAuthorization === false
                                ? "false"
                                : ""
                          }
                          onChange={(e) =>
                            setFieldValue(
                              `patientInsurances[${ind}].priorAuthorization`,
                              e.target.value === "true"
                            )
                          }
                        >
                          <FormControlLabel
                            value="true"
                            control={<Radio size="small" />}
                            label="Yes"
                          />
                          <FormControlLabel
                            value="false"
                            control={<Radio size="small" />}
                            label="No"
                          />
                        </RadioGroup>
                      </Grid>
                      <Grid item xs={3}>
                        <CustomSelect
                          label="Verification Status"
                          name={`patientInsurances[${ind}].verificationStatus`}
                          handleBlur={handleBlur}
                          options={[
                            { value: "DONE", label: "Done" },
                            { value: "PENDING", label: "Pending" },
                          ]}
                          value={
                            values.patientInsurances?.[ind]?.verificationStatus
                          }
                          onChange={(value) => {
                            setFieldValue(
                              `patientInsurances[${ind}].verificationStatus`,
                              value
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              */}
              <Grid item xs={3}>
                <NumberInput
                  label="Deductible"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].deductible`,
                    value: values.patientInsurances?.[ind]?.deductible ?? "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <NumberInput
                  label="Deductible Remaining"
                  textFieldProps={{
                    name: `patientInsurances[${ind}].deductibleAmount`,
                    value:
                      values.patientInsurances?.[ind]?.deductibleAmount ?? "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                  }}
                />
              </Grid>
            </Grid>
            {(() => {
              const insuranceErrors = (errors?.patientInsurances?.[ind] ??
                {}) as Record<string, any>;
              const hasSubscriberError = !!insuranceErrors?.subscriberAddress;
              const hasEmployerError = !!insuranceErrors?.employerAddress;
              const hasCardError =
                !!insuranceErrors?.frontPhoto || !!insuranceErrors?.backPhoto;
              const subscriberKey = `${ind}-subscriber`;
              const employerKey = `${ind}-employer`;
              const cardKey = `${ind}-card`;
              return (
                <>
                  <Accordion
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                    }}
                    expanded={
                      hasSubscriberError || !!accordionOpenMap[subscriberKey]
                    }
                    onChange={(_, exp) =>
                      setAccordionOpenMap((prev) => ({
                        ...prev,
                        [subscriberKey]: exp,
                      }))
                    }
                  >
                    <AccordionSummary
                      sx={{ paddingLeft: 0 }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`subscriber-info-content-${ind}`}
                      id={`subscriber-info-header-${ind}`}
                    >
                      <Typography fontWeight={500}>
                        Subscriber Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0 }}>
                      <SubscriberInformation
                        formik={formik}
                        ind={ind}
                        patientData={patientData}
                      />
                    </AccordionDetails>
                  </Accordion>
                  <Accordion
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                    }}
                    expanded={
                      hasEmployerError || !!accordionOpenMap[employerKey]
                    }
                    onChange={(_, exp) =>
                      setAccordionOpenMap((prev) => ({
                        ...prev,
                        [employerKey]: exp,
                      }))
                    }
                  >
                    <AccordionSummary
                      sx={{ paddingLeft: 0 }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`employer-info-content-${ind}`}
                      id={`employer-info-header-${ind}`}
                    >
                      <Typography fontWeight={500}>
                        Employer Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0 }}>
                      <EmployerInfo ind={ind} formik={formik} />
                    </AccordionDetails>
                  </Accordion>
                  <Accordion
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:before": {
                        display: "none",
                      },
                    }}
                    expanded={hasCardError || !!accordionOpenMap[cardKey]}
                    onChange={(_, exp) =>
                      setAccordionOpenMap((prev) => ({
                        ...prev,
                        [cardKey]: exp,
                      }))
                    }
                  >
                    <AccordionSummary
                      sx={{ paddingLeft: 0 }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`insurance-card-${ind}`}
                      id={`insurance-card-${ind}`}
                    >
                      <Typography fontWeight={500}>
                        Upload Insurance Card
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0 }}>
                      <Grid item container spacing={3} xs={12} mt={0.2}>
                        <Grid item xs={5}>
                          <DropZone
                            value={values?.patientInsurances?.[ind]?.frontPhoto}
                            error={
                              (errors?.patientInsurances?.[ind] as any)
                                ?.frontPhoto
                            }
                            supportedFile=".jpg,.jpeg,.png,.svg"
                            width={"21em"}
                            height={"12em"}
                            solidBorder
                            content="Click here to Upload Front Side, Drag and Drop Your File or Browse"
                            backgroundColor="#EEEEEE"
                            onClick={async (files) => {
                              if (files) {
                                const file = files[0];
                                if (!supportedFileTypes?.includes(file?.type)) {
                                  formik.setFieldValue(
                                    `patientInsurances[${ind}].frontPhoto`,
                                    "otherFile",
                                    true
                                  );
                                } else if (file.size > 5 * 1024 * 1024) {
                                  formik.setFieldValue(
                                    `patientInsurances[${ind}].frontPhoto`,
                                    "size5",
                                    true
                                  );
                                } else {
                                  const base64File = await convertToBase64(
                                    files[0]
                                  );
                                  formik.setFieldValue(
                                    `patientInsurances[${ind}].frontPhoto`,
                                    base64File
                                  );
                                }
                              } else
                                formik.setFieldValue(
                                  `patientInsurances[${ind}].frontPhoto`,
                                  null
                                );

                              formik.validateField("patientInsurances");
                            }}
                            uploadIcon={
                              <CloudUploadIcon
                                sx={{ fontSize: "3em", color: "#2879C9" }}
                              />
                            }
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <DropZone
                            value={values?.patientInsurances?.[ind]?.backPhoto}
                            error={
                              (errors?.patientInsurances?.[ind] as any)
                                ?.backPhoto
                            }
                            supportedFile=".jpg,.jpeg,.png,.svg"
                            width={"21em"}
                            height={"12em"}
                            solidBorder
                            backgroundColor="#EEEEEE"
                            content="Click here to Upload Back Side, Drag and Drop Your File or Browse"
                            onClick={async (files) => {
                              if (files) {
                                const file = files[0];

                                if (!supportedFileTypes?.includes(file?.type)) {
                                  formik.setFieldValue(
                                    `patientInsurances[${ind}].backPhoto`,
                                    "otherFile",
                                    true
                                  );
                                } else if (file.size > 5 * 1024 * 1024) {
                                  formik.setFieldValue(
                                    `patientInsurances[${ind}].backPhoto`,
                                    "size5",
                                    true
                                  );
                                } else {
                                  const base64File =
                                    await convertToBase64(file);
                                  formik.setFieldValue(
                                    `patientInsurances[${ind}].backPhoto`,
                                    base64File
                                  );
                                }
                              } else
                                formik.setFieldValue(
                                  `patientInsurances[${ind}].backPhoto`,
                                  null
                                );
                              formik.validateField("patientInsurances");
                            }}
                            uploadIcon={
                              <CloudUploadIcon
                                sx={{ fontSize: "3em", color: "#2879C9" }}
                              />
                            }
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </>
              );
            })()}
          </OutlineCard>
        </Box>
      ))}
      {!!formik.values?.patientInsurances?.length &&
        formik.values?.patientInsurances?.length < 4 &&
        addOneInsurance && (
          <StyledButtonV2
            onClick={() => {
              formik.setFieldValue("patientInsurances", [
                ...(formik.values.patientInsurances ?? []),
                INITIAL_PATIENT_INSURANCE,
              ]);
            }}
            shouldAddIcon
            variant="text"
            label={"Add Another Insurance"}
          />
        )}
    </Box>
  );
};

export default Insurance;

const supportedFileTypes = ["image/jpeg", "image/png", "image/svg+xml"];
